import { ReactNode } from 'react';
import { motion, MotionProps } from 'motion/react';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { twMerge } from 'tailwind-merge';
import { Card } from '@/components/ui/daisy';
import { UseFlashcardSwipeReturn } from './useFlashcardSwipe';
import { FlashcardSwipeColorOverlay } from './FlashcardSwipeFeedback';

type FlashcardFlipCardProps = {
  front?: ReactNode;
  back?: ReactNode;
  showBack: boolean;
  showSwipeFeedback: boolean;
  onFlip?: () => void;
  swipe?: UseFlashcardSwipeReturn;
  draggable?: boolean;
};

const flashcardFaceClass =
  'absolute inset-0 rounded-2xl overflow-x-hidden overflow-y-auto scrollbar-none shadow-md grid content-center-safe backface-hidden';

export const FlashcardFlipCard = ({
  front,
  back,
  showBack,
  showSwipeFeedback,
  onFlip,
  swipe,
  draggable = false,
}: FlashcardFlipCardProps) => {
  const dragMotion: MotionProps = swipe
    ? {
        style: {
          x: swipe.x,
          rotate: swipe.rotate,
          opacity: swipe.opacity,
          touchAction: 'pan-y',
        },
        drag: draggable && showBack ? 'x' : false,
        dragElastic: 0.6,
        onDragEnd: swipe.handleDragEnd,
        onTap: draggable && !showBack ? onFlip : undefined,
      }
    : {};

  return (
    <motion.div {...dragMotion} className="size-full perspective-[1600px]">
      <motion.div
        className="relative size-full transform-3d"
        initial={false}
        animate={{ rotateY: showBack ? 180 : 0 }}
        transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
      >
        <Card className={twMerge(flashcardFaceClass, 'bg-base-300')}>
          <Card.Body>{front}</Card.Body>
        </Card>
        <Card
          className={twMerge(flashcardFaceClass, 'bg-accent-content rotate-y-180')}
        >
          <Card.Body className="z-10">{back}</Card.Body>
          <ConditionalRenderer condition={showSwipeFeedback}>
            <FlashcardSwipeColorOverlay
              successOpacity={swipe?.successOpacity}
              errorOpacity={swipe?.errorOpacity}
            />
          </ConditionalRenderer>
        </Card>
      </motion.div>
    </motion.div>
  );
};
